import { supportedReasoningEfforts } from "@arti/ai-models-config/reasoningEffortCapability";
import { dashboardReasoningEffortSchema } from "@arti/schemas/dashboard";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useDashboardAuth } from "../../../hooks/useDashboardAuth";
import { useOrgMcpServers } from "../../../hooks/useOrgMcpServers";
import { Button } from "../../common/Button";
import { Text } from "../../common/Text";
import {
  type BlackboardInstance,
  type ReasoningEffort,
} from "../blackboardModels";
import { ModelPicker } from "../ModelPicker";
import {
  formatTokenCount,
  readOptionalStringProperty,
  sliderFillStyle,
} from "./helpers";

// ---------------------------------------------------------------------------
// OverrideControls — per-instance overrides: compact / heartbeat / schedule /
// reasoning effort. Each field is independently saveable + resettable.
// ---------------------------------------------------------------------------

/** Lifted verbatim from the former InfoTab god component. */
/**
 * Which MCP servers this actor reaches, and whether that came from its template
 * or from itself.
 *
 * The three states are shown rather than inferred, because they are genuinely
 * different and the difference is invisible otherwise: inheriting, overriding
 * with a set, and overriding with nothing. That last one matters -- an actor
 * that must NOT reach a server its template attaches is a real instruction, not
 * an empty field, so unticking everything is an override, and only "reset"
 * returns to inheriting.
 */
/** Matches the lowercase status wording every other override row uses. */
function overrideLabel(isOverridden: boolean, count: number): string {
  if (!isOverridden) {
    return "from template";
  }
  return count === 0 ? "override · none" : "override";
}

const McpServerOverrideSection: FC<{
  onSave: (body: Record<string, unknown>) => Promise<void>;
  overrides: NonNullable<BlackboardInstance["overrides"]>;
  saving: boolean;
}> = ({ onSave, overrides, saving }) => {
  const auth = useDashboardAuth();
  const { servers } = useOrgMcpServers(auth.organizationId);
  const resolved = overrides.mcpServerIds ?? [];
  const isOverridden = overrides.mcpServerIdsSource === "instance";
  const offered = servers.filter(
    (server) => server.status === "active" || resolved.includes(server.id),
  );

  if (offered.length === 0) {
    return null;
  }

  const toggle = async (id: string) => {
    // Toggling while inheriting starts the override from what was inherited,
    // so one click means "the template's set, but not this one" rather than
    // "only this one".
    const next = resolved.includes(id)
      ? resolved.filter((current) => current !== id)
      : [...resolved, id];
    await onSave({ mcpServerIdsOverride: next });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <Text className="font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
          MCP Servers
        </Text>
        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          {overrideLabel(isOverridden, resolved.length)}
        </span>
        {isOverridden && (
          <button
            className="ml-auto font-mono text-[9px] uppercase tracking-[0.1em] text-slate-500 hover:text-sky-600 disabled:opacity-50 dark:text-slate-400 dark:hover:text-sky-400"
            disabled={saving}
            onClick={() => void onSave({ mcpServerIdsOverride: null })}
            type="button"
          >
            Reset to template
          </button>
        )}
      </div>
      {offered.map((server) => {
        const attached = resolved.includes(server.id);
        return (
          <button
            className="flex items-center justify-between gap-2 font-mono text-[10px] disabled:opacity-50"
            disabled={saving}
            key={server.id}
            onClick={() => void toggle(server.id)}
            title={server.url}
            type="button"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className={
                  attached
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-slate-400 dark:text-slate-600"
                }
              >
                {attached ? "\u25a3" : "\u25a2"}
              </span>
              <span
                className={`truncate ${
                  attached
                    ? "text-slate-700 dark:text-slate-200"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {server.display_name}
              </span>
            </span>
            <span className="shrink-0 text-slate-400 dark:text-slate-500">
              {server.status === "disabled" ? "disabled" : server.slug}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const OverrideControls: FC<{
  instanceId: string;
  overrides: NonNullable<BlackboardInstance["overrides"]>;
  /** Instance model ref (null = runtime default). Editable via PATCH. */
  modelRef?: string | null;
  /** Instance cooldown in ms (null = system default). Editable via PATCH. */
  tickIntervalMs?: number | null;
  onSaved?: () => void;
}> = ({ instanceId, overrides, modelRef, tickIntervalMs, onSaved }) => {
  const maxThreshold = Math.floor(overrides.contextWindowTokens * 0.9);
  const [compactValue, setCompactValue] = useState<number>(
    overrides.compactContextGrowthThreshold,
  );
  const [modelValue, setModelValue] = useState<string>(modelRef ?? "");
  const [tickValue, setTickValue] = useState<string>(
    tickIntervalMs != null ? String(tickIntervalMs) : "",
  );
  const [heartbeatValue, setHeartbeatValue] = useState<string>(
    overrides.heartbeatIntervalS != null
      ? String(overrides.heartbeatIntervalS)
      : "",
  );
  const [scheduleValue, setScheduleValue] = useState<string>(
    overrides.consolidationSchedule ?? "",
  );
  const [reasoningEffortValue, setReasoningEffortValue] = useState<
    "" | ReasoningEffort
  >(overrides.reasoningEffortOverride ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compactDirty =
    compactValue !==
    (overrides.compactContextGrowthThresholdOverride ??
      overrides.compactContextGrowthThreshold);
  const persistedHeartbeatValue = (() => {
    if (overrides.heartbeatIntervalSOverride != null) {
      return String(overrides.heartbeatIntervalSOverride);
    }
    if (overrides.heartbeatIntervalS != null) {
      return String(overrides.heartbeatIntervalS);
    }
    return "";
  })();
  const heartbeatDirty = heartbeatValue !== persistedHeartbeatValue;
  const scheduleDirty =
    scheduleValue !==
    (overrides.consolidationScheduleOverride ??
      overrides.consolidationSchedule ??
      "");
  const persistedReasoningEffortValue = overrides.reasoningEffortOverride ?? "";
  const reasoningEffortDirty =
    reasoningEffortValue !== persistedReasoningEffortValue;
  const modelDirty = modelValue !== (modelRef ?? "");
  const effortOptions = useMemo(
    () => supportedReasoningEfforts(modelValue),
    [modelValue],
  );
  const selectedEffort = reasoningEffortValue;
  const effortUnsupported =
    reasoningEffortValue !== "" &&
    !effortOptions.some((option) => option.value === selectedEffort);
  const previousModelValue = useRef(modelValue);
  useEffect(() => {
    if (previousModelValue.current === modelValue) {
      return;
    }
    previousModelValue.current = modelValue;
    if (effortUnsupported) {
      setReasoningEffortValue("");
    }
  }, [modelValue, effortUnsupported]);
  const persistedTickValue =
    tickIntervalMs != null ? String(tickIntervalMs) : "";
  const tickDirty = tickValue !== persistedTickValue;
  const anyDirty =
    compactDirty ||
    heartbeatDirty ||
    scheduleDirty ||
    reasoningEffortDirty ||
    modelDirty ||
    tickDirty;

  const saveField = useCallback(
    async (body: Record<string, unknown>) => {
      setError(null);
      setSaving(true);
      try {
        const resp = await fetch(`/user/instances/${instanceId}`, {
          body: JSON.stringify(body),
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          method: "PATCH",
        });
        if (!resp.ok) {
          setError(
            readOptionalStringProperty(
              await resp.json().catch(() => ({})),
              "error",
            ) ?? `Save failed (${resp.status})`,
          );
          return;
        }
        onSaved?.();
      } finally {
        setSaving(false);
      }
    },
    [instanceId, onSaved],
  );

  return (
    <div className="flex flex-col gap-3 p-4">
      <Text
        as="p"
        variant="label"
        className="text-slate-400 dark:text-slate-500"
      >
        Overrides
      </Text>

      {/* Model */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Model
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {modelRef ? "set on instance" : "runtime default"}
          </span>
        </div>
        <ModelPicker
          value={modelValue}
          onChange={setModelValue}
          noneOptionLabel="Runtime default (Claude Sonnet 4.6)"
        />
        {modelDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void saveField({ modelRef: modelValue || null })}
              loading={saving}
              loadingText="Saving…"
            >
              Save model
            </Button>
            <button
              type="button"
              className="font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() => setModelValue(modelRef ?? "")}
            >
              Cancel
            </button>
          </div>
        )}
        <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          Applies from the next activation.
        </p>
      </div>

      {/* Cooldown (tick interval) */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Cooldown (ms)
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {tickIntervalMs != null ? "set on instance" : "default"}
          </span>
        </div>
        <input
          type="number"
          min={1000}
          step={1000}
          placeholder="e.g. 15000 · blank = default"
          value={tickValue}
          onChange={(e) => setTickValue(e.target.value)}
          className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200"
        />
        {tickDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const parsed = tickValue.trim()
                  ? Number.parseInt(tickValue, 10)
                  : null;
                if (
                  parsed !== null &&
                  (!Number.isFinite(parsed) || parsed < 1000)
                ) {
                  setError("Cooldown must be at least 1000 ms");
                  return;
                }
                void saveField({ tickIntervalMs: parsed });
              }}
              loading={saving}
              loadingText="Saving…"
            >
              Save cooldown
            </Button>
            <button
              type="button"
              className="font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() => setTickValue(persistedTickValue)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Compact threshold */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Compact threshold
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {formatTokenCount(compactValue)} / {formatTokenCount(maxThreshold)}{" "}
            max ·{" "}
            {overrides.compactContextGrowthThresholdSource === "instance"
              ? "override"
              : "from template"}
          </span>
        </div>
        <input
          type="range"
          min={1000}
          max={maxThreshold}
          step={1000}
          value={compactValue}
          onChange={(e) => setCompactValue(Number.parseInt(e.target.value, 10))}
          className="slider-square w-full"
          style={sliderFillStyle(compactValue, 1000, maxThreshold)}
        />
        {compactDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                void saveField({
                  compactContextGrowthThresholdOverride: compactValue,
                })
              }
              loading={saving}
              loadingText="Saving…"
            >
              Save override
            </Button>
            <button
              type="button"
              className="font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() =>
                setCompactValue(overrides.compactContextGrowthThreshold)
              }
            >
              Cancel
            </button>
          </div>
        )}
        {overrides.compactContextGrowthThresholdSource === "instance" &&
          !compactDirty && (
            <button
              type="button"
              className="self-start font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() =>
                void saveField({ compactContextGrowthThresholdOverride: null })
              }
            >
              Reset to template
            </button>
          )}
      </div>

      {/* Heartbeat interval */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Heartbeat (seconds)
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {overrides.heartbeatIntervalSSource ?? "disabled"}
          </span>
        </div>
        <input
          type="number"
          min={5}
          max={3600}
          step={5}
          placeholder="e.g. 60"
          value={heartbeatValue}
          onChange={(e) => setHeartbeatValue(e.target.value)}
          className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200"
        />
        {heartbeatDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const parsed = heartbeatValue.trim()
                  ? Number.parseInt(heartbeatValue, 10)
                  : null;
                void saveField({ heartbeatIntervalSOverride: parsed });
              }}
              loading={saving}
              loadingText="Saving…"
            >
              Save override
            </Button>
          </div>
        )}
        {overrides.heartbeatIntervalSSource === "instance" &&
          !heartbeatDirty && (
            <button
              type="button"
              className="self-start font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() => {
                setHeartbeatValue(
                  overrides.heartbeatIntervalS != null
                    ? String(overrides.heartbeatIntervalS)
                    : "",
                );
                void saveField({ heartbeatIntervalSOverride: null });
              }}
            >
              Reset to template
            </button>
          )}
      </div>

      {/* Consolidation schedule */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Consolidation schedule
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {overrides.consolidationScheduleSource ?? "disabled"}
          </span>
        </div>
        <input
          type="text"
          placeholder="interval:180 | daily:03:00 | cron:0 3 * * *"
          value={scheduleValue}
          onChange={(e) => setScheduleValue(e.target.value)}
          className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200"
        />
        {scheduleDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                void saveField({
                  consolidationScheduleOverride:
                    scheduleValue.trim() === "" ? null : scheduleValue.trim(),
                })
              }
              loading={saving}
              loadingText="Saving…"
            >
              Save override
            </Button>
          </div>
        )}
        {overrides.consolidationScheduleSource === "instance" &&
          !scheduleDirty && (
            <button
              type="button"
              className="self-start font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() => {
                setScheduleValue(overrides.consolidationSchedule ?? "");
                void saveField({ consolidationScheduleOverride: null });
              }}
            >
              Reset to template
            </button>
          )}
      </div>

      {/* Reasoning effort */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            Reasoning effort
          </label>
          <span className="font-mono text-[10px] text-slate-400">
            {overrides.reasoningEffortSource ?? "provider default"}
          </span>
        </div>
        <select
          value={selectedEffort}
          onChange={(event) => {
            const { value } = event.target;
            setReasoningEffortValue(
              value === ""
                ? value
                : dashboardReasoningEffortSchema.parse(value),
            );
          }}
          className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200"
        >
          <option value="">inherit (template)</option>
          {effortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {effortUnsupported && (
            <option value={reasoningEffortValue}>
              {reasoningEffortValue} (unsupported by this model)
            </option>
          )}
        </select>
        {reasoningEffortDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                void saveField({
                  reasoningEffortOverride: reasoningEffortValue || null,
                })
              }
              loading={saving}
              loadingText="Saving…"
            >
              Save override
            </Button>
          </div>
        )}
        {overrides.reasoningEffortSource === "instance" &&
          !reasoningEffortDirty && (
            <button
              type="button"
              className="self-start font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              onClick={() => {
                setReasoningEffortValue("");
                void saveField({ reasoningEffortOverride: null });
              }}
            >
              Reset to template
            </button>
          )}
      </div>

      <McpServerOverrideSection
        onSave={saveField}
        overrides={overrides}
        saving={saving}
      />

      {error && (
        <p className="font-mono text-[10px] text-rose-500 dark:text-rose-400">
          {error}
        </p>
      )}
      {!anyDirty && !error && (
        <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          Changes apply immediately on the next activation. Reset clears the
          override and inherits from the template.
        </p>
      )}
    </div>
  );
};
